import {useUrlParams} from 'services/hooks'
import TablePaginationRaw from '@mui/material/TablePagination'
import {PAGE_SIZES} from 'constants/tables'
import {useCallback, useState, useEffect, useRef} from 'react'

const DELAY = 1000 // 1000ms delay

const TablePagination = ({
    itemName,
    paginationType, // 'local', 'external' (server) or null
    totalItems,
}) => {
    const {urlParams, setUrlParam} = useUrlParams()
    const {[`${itemName}_page`]: urlPage, [`${itemName}_pageSize`]: urlPageSize} = urlParams
    
    // Local UI state for immediate feedback
    const [localPage, setLocalPage] = useState(Number(urlPage || 0))
    const [localPageSize, setLocalPageSize] = useState(Number(urlPageSize || PAGE_SIZES[2]))
    
    // Refs for tracking timeouts
    const pageTimeoutRef = useRef(null)
    const pageSizeTimeoutRef = useRef(null)
    
    // Keep local state in sync with URL
    useEffect(() => {
        setLocalPage(Number(urlPage || 0))
    }, [urlPage])
    
    useEffect(() => {
        setLocalPageSize(Number(urlPageSize || PAGE_SIZES[2]))
    }, [urlPageSize])



    const handlePageChange = useCallback((_, p) => {
        setLocalPage(p)  // Update UI immediately
        
        // Clear any pending timeout
        if (pageTimeoutRef.current) {
            clearTimeout(pageTimeoutRef.current)
        }
        
        // Set new timeout for URL update
        pageTimeoutRef.current = setTimeout(() => {
            setUrlParam(`${itemName}_page`, p)
            pageTimeoutRef.current = null
        }, DELAY)
    }, [itemName, setUrlParam])

    const handleRowsPerPageChange = useCallback((e) => {
        const newSize = parseInt(e.target.value, 10)
        setLocalPageSize(newSize)  // Update UI immediately
        setLocalPage(0)  // Reset to first page
        
        // Clear any pending timeouts
        if (pageSizeTimeoutRef.current) {
            clearTimeout(pageSizeTimeoutRef.current)
        }
        if (pageTimeoutRef.current) {
            clearTimeout(pageTimeoutRef.current)
        }
        
        // Set new timeout for URL updates
        pageSizeTimeoutRef.current = setTimeout(() => {
            setUrlParam(`${itemName}_pageSize`, newSize)
            setUrlParam(`${itemName}_page`, 0)
            pageSizeTimeoutRef.current = null
        }, DELAY)
    }, [itemName, setUrlParam])
    
    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (pageTimeoutRef.current) {
                clearTimeout(pageTimeoutRef.current)
            }
            if (pageSizeTimeoutRef.current) {
                clearTimeout(pageSizeTimeoutRef.current)
            }
        }
    }, [])

    if (!paginationType) {
        return null
    }

    return (
        <TablePaginationRaw
            rowsPerPageOptions={PAGE_SIZES}
            component="div"
            count={totalItems || 0}
            rowsPerPage={localPageSize}
            page={localPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
        />
    )
}

export default TablePagination
